import React, { useLayoutEffect, useRef, useState } from 'react';
import { Chessground } from 'chessground';
import 'chessground/assets/chessground.base.css';
import 'chessground/assets/chessground.brown.css';
import 'chessground/assets/chessground.cburnett.css';
import '../css/classic-chessboard.css';

// The same Chessground board and standard pieces used by Chesshood Training.
// Only this adapter is new: rules and lesson decisions stay with the caller.
export default function ClassicChessboard({ config, label = 'Chessboard' }) {
  const element = useRef(null), api = useRef(null), latest = useRef(config);
  const [keyboardSquare, setKeyboardSquare] = useState('e2');
  latest.current = config;
  function controlledConfig(value) {
    return {...value, movable: {...value.movable, events: {...value.movable?.events,
      after: (...args) => {
        latest.current.movable?.events?.after?.(...args);
        // Promotion and lesson validation may reject or defer the visual move.
        // Restore the authoritative position even when React state is unchanged.
        api.current?.set({fen: latest.current.fen, turnColor: latest.current.turnColor});
      }
    }}};
  }
  useLayoutEffect(() => {
    api.current = Chessground(element.current, controlledConfig(latest.current));
    return () => { api.current?.destroy(); api.current = null; };
  }, []);
  useLayoutEffect(() => { api.current?.set(controlledConfig(config)); }, [config]);
  function keyboard(event) {
    const delta = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] }[event.key];
    if (delta) {
      event.preventDefault();
      const direction = config.orientation === 'black' ? -1 : 1;
      const file = Math.max(0, Math.min(7, keyboardSquare.charCodeAt(0) - 97 + delta[0] * direction));
      const rank = Math.max(1, Math.min(8, Number(keyboardSquare[1]) + delta[1] * direction));
      setKeyboardSquare('abcdefgh'[file] + rank);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!config.viewOnly) api.current?.selectSquare(keyboardSquare);
    } else if (event.key === 'Escape') {
      api.current?.cancelMove();
    }
  }
  return <div className="ch-classic-board" role="group" aria-label={label}>
    <div ref={element} tabIndex={0} onKeyDown={keyboard} aria-label={`${label}. Arrow keys choose a square; Enter selects or moves a piece.`} />
    <span className="ch-classic-sr" aria-live="polite">{keyboardSquare}</span>
  </div>;
}
